import { HttpErrorResponse } from '@angular/common/http';

import { FinancingHold, FinancingHoldScope } from 'src/app/api/models';

/**
 * The scope of a financing hold as the Console names it: what the hold was
 * placed on (add-financing-holds, design D1).
 */
export const FINANCING_HOLD_SCOPE_LABELS: Readonly<Record<FinancingHoldScope, string>> = {
  [FinancingHoldScope.Client]: 'Client',
  [FinancingHoldScope.EndCustomer]: 'End customer',
  [FinancingHoldScope.Invoice]: 'Invoice',
};

export function financingHoldScopeLabel(hold: FinancingHold): string {
  return FINANCING_HOLD_SCOPE_LABELS[hold.scope] ?? hold.scope;
}

/** A hold's scope and reason: `Client: Waiting for the report`. */
export function describeFinancingHold(hold: FinancingHold): string {
  return `${financingHoldScopeLabel(hold)}: ${hold.reason}`;
}

/**
 * The message a dialog shows for a refused request: the server's own
 * message when the body carries one (every structured 422 does), else the
 * fallback.
 */
export function refusalMessage(err: unknown, fallback: string): string {
  const message = (err as { error?: { message?: unknown } } | null)?.error?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

/**
 * The tooltip on a control a hold disables: every applying hold's scope and
 * reason, `On financing hold — Client: …; Invoice: …`.
 */
export function financingHoldsTooltip(holds: FinancingHold[] | null | undefined): string {
  return `On financing hold — ${(holds ?? []).map(describeFinancingHold).join('; ')}`;
}

/**
 * The holds a `422 financing_on_hold` refusal lists in `details.holds`, or
 * null for any other error. Payment create, retry and resubmit, and the
 * workflow event's accept refuse with it.
 */
export function financingOnHoldRefusal(err: unknown): FinancingHold[] | null {
  if (!(err instanceof HttpErrorResponse) || err.status !== 422) {
    return null;
  }
  const body = err.error as { error_code?: unknown; details?: { holds?: unknown } } | null;
  if (body?.error_code !== 'financing_on_hold') {
    return null;
  }
  const holds = body.details?.holds;
  return Array.isArray(holds) ? (holds as FinancingHold[]) : [];
}

/**
 * The text that shows a `financing_on_hold` refusal with its holds:
 * `<lead>: the invoice is on financing hold.` and a line per hold.
 */
export function financingOnHoldRefusalText(lead: string, holds: FinancingHold[]): string {
  return [`${lead}: the invoice is on financing hold.`, ...holds.map((hold) => `• ${describeFinancingHold(hold)}`)].join(
    '\n',
  );
}
